import { useEffect, useState } from "react";
import { Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const FIELDS = [
  ["retention_days_deleted", "Books in Trash", "Erased permanently this many days after you delete them."],
  ["retention_audit_days", "Activity log entries", "Older entries are removed."],
  ["retention_jobs_days", "Finished AI job records & saved AI responses", "Only finished, failed or cancelled jobs. Your manuscripts and versions are never touched."],
  ["retention_readers_days", "Unsubscribed readers & finished reminders", "Readers who unsubscribed, and reminders already sent or cancelled."],
  ["retention_exports_days", "Old export files", "Export history and older generated files. The newest file of each edition is always kept."],
];
const LABELS = { books_erased: "books erased", activity_entries: "activity entries", job_records: "job records", ai_responses: "saved AI responses", unsubscribed_readers: "unsubscribed readers", finished_reminders: "finished reminders", export_history: "export history entries", export_files: "old export files" };

export function RetentionPanel({ isOwner }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/retention").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!isOwner) return <div className="panel p-6 text-sm text-slate-400" data-testid="retention-owner-only">Only the Master Owner can change data retention.</div>;
  if (!r) return null;
  const save = async () => { try { const { data } = await api.put("/admin/retention", Object.fromEntries(FIELDS.map(([k]) => [k, Number(r[k]) || 0]))); setR(data); toast.success("Retention saved"); } catch (e) { toast.error(errMsg(e)); } };
  const run = async () => {
    if (!window.confirm("Run cleanup now? Anything older than your settings is permanently removed.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/retention/run"); setR({ ...r, last_run: data }); toast.success("Cleanup finished"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const res = r.last_run?.result || {};
  return (
    <div className="space-y-4" data-testid="retention-panel">
      <div className="panel p-6">
        <p className="eyebrow mb-1">Keep data for</p>
        <p className="text-xs text-slate-500 mb-4">Days. 0 keeps it forever. Cleanup runs automatically every night.</p>
        {FIELDS.map(([k, label, hint]) => (
          <div key={k} className="flex flex-wrap items-center justify-between gap-4 py-3 border-b border-slate-800">
            <div><p className="text-sm text-slate-200">{label}</p><p className="text-xs text-slate-500 max-w-xl">{hint}</p></div>
            <div className="flex items-center gap-2"><Input type="number" min="0" max="3650" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })} className="w-24 h-9" data-testid={`retention-${k}`} /><span className="text-xs text-slate-500">days</span></div>
          </div>
        ))}
        <div className="flex gap-2 mt-5"><Button onClick={save} className="bg-amber-600 hover:bg-amber-500" data-testid="retention-save">Save</Button>
          <Button onClick={run} disabled={busy} variant="outline" className="border-red-800 text-red-300" data-testid="retention-run-now"><Eraser size={14} className="mr-1" />{busy ? "Cleaning…" : "Run cleanup now"}</Button></div>
      </div>
      <div className="panel p-5" data-testid="retention-last-run">
        <p className="eyebrow mb-2">Last cleanup</p>
        {r.last_run ? <><p className="text-xs text-slate-400 mb-2">{fmtDate(r.last_run.created_at)} · {r.last_run.by === "system" ? "automatic" : "run by you"}</p>
          <div className="flex flex-wrap gap-2">{Object.entries(res).map(([k, v]) => <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">{v} {LABELS[k] || k}</span>)}</div></> : <p className="text-xs text-slate-500">Not run yet.</p>}
        <p className="text-[11px] text-slate-500 mt-3">Erased files are overwritten in storage so their contents can't be recovered.</p>
      </div>
    </div>
  );
}
